import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { CurrencyEur, PaperPlaneTilt, CheckCircle, Clock, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * A5 — D29 / P4#5 : l'entreprise dépose sa situation du mois (« Mes situations »).
 * Les postes viennent de SON marché ; elle indique l'avancement cumulé de chaque poste en %.
 * La situation passe « Déposée — à vérifier » chez l'architecte, qui la vérifie et la
 * certifie sous 15 jours (NF P 03-001) ; l'entreprise voit ensuite l'échéance de paiement.
 * Source : GET/POST /api/chantiers/{id}/mes-situations.
 */
const fmtEur = (v) => `${Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(String(v).length === 10 ? `${v}T12:00:00` : v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR');
};
const moisCourant = () => new Date().toISOString().slice(0, 7);
const moisFr = (m) => {
  const x = /^(\d{4})-(\d{2})/.exec(m || '');
  return x ? new Date(Number(x[1]), Number(x[2]) - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) : (m || '');
};
const COULEUR = {
  deposee: 'bg-[#E3F2FD] text-[#1E3A5F]',
  rejetee: 'bg-[#FFEBEE] text-[#8B3A3A]',
  validee: 'bg-[#E8F5E9] text-[#2D6A4F]',
  payee: 'bg-[#2D6A4F] text-white',
};

function Depot({ chantierId, lot, delai, onDone }) {
  const [avct, setAvct] = useState(() => Object.fromEntries(lot.postes.map((p) => [p.poste_id, p.avancement_pct])));
  const [mois, setMois] = useState(lot.situation_ouverte?.mois || moisCourant());
  const [commentaire, setCommentaire] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const montantMois = lot.postes.reduce((s, p) => {
    const v = Math.min(100, Math.max(0, Number(avct[p.poste_id]) || 0));
    return s + Math.max(0, v - p.avancement_precedent_pct) / 100 * p.montant_ht;
  }, 0);

  const deposer = async () => {
    setEnvoi(true);
    try {
      await axios.post(`${API_URL}/api/chantiers/${chantierId}/mes-situations`, {
        marche_id: lot.marche_id, mois, commentaire,
        postes: lot.postes.map((p) => ({ poste_id: p.poste_id, avancement_pct: Number(avct[p.poste_id]) || 0 })),
      }, { withCredentials: true });
      toast.success("Situation déposée : l'architecte la vérifie et la certifie.");
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Dépôt impossible pour le moment.');
    } finally { setEnvoi(false); }
  };

  const ouverte = lot.situation_ouverte;
  return (
    <section className="border border-[#E0E0DB] bg-white" data-testid={`depot-lot-${lot.lot_code}`}>
      <div className="px-4 py-3 border-b border-[#E0E0DB] flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-semibold text-[#2C2C2C]">Lot {lot.lot_code} — {lot.lot_nom}</p>
          <p className="text-xs text-[#6B6B6B]">Votre marché : {fmtEur(lot.montant_marche_ht)} HT</p>
        </div>
        {ouverte && (
          <span className={`text-xs px-2 py-1 ${COULEUR[ouverte.statut] || 'bg-[#FFF8E1] text-[#B8860B]'}`}>
            Situation n° {ouverte.numero} · {ouverte.statut_libelle}
          </span>
        )}
      </div>
      {ouverte?.motif_rejet && (
        <p className="mx-4 mt-3 text-sm text-[#8B3A3A] bg-[#FFEBEE] border border-[#8B3A3A] p-2 flex items-start gap-2">
          <WarningCircle size={16} className="flex-shrink-0 mt-0.5" /> À corriger : {ouverte.motif_rejet}
        </p>
      )}
      {ouverte?.statut === 'deposee' && (
        <p className="mx-4 mt-3 text-sm text-[#1E3A5F]">
          Déposée le {fmtDate(ouverte.deposee_le)} : l'architecte la vérifie avant le {fmtDate(ouverte.verifier_avant)}.
          Vous pouvez encore la corriger et la déposer à nouveau.
        </p>
      )}
      {lot.raison && <p className="mx-4 mt-3 text-xs text-[#6B6B6B]">{lot.raison}</p>}
      {lot.postes.length > 0 && (
        <div className="p-4 space-y-3">
          <ul className="divide-y divide-[#F0F0EE] border border-[#E0E0DB]">
            {lot.postes.map((p) => (
              <li key={p.poste_id} className="p-3 flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#2C2C2C]">{p.designation}</p>
                  <p className="text-xs text-[#6B6B6B]">
                    {String(p.quantite_marche).replace('.', ',')} {p.unite} · {fmtEur(p.montant_ht)} HT
                    {p.avancement_precedent_pct > 0 && ` · déjà certifié : ${p.avancement_precedent_pct} %`}
                  </p>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <span className="text-xs text-[#6B6B6B]">Avancement cumulé</span>
                  <input type="number" min={p.avancement_precedent_pct} max={100} step="1" disabled={!lot.peut_deposer}
                    value={avct[p.poste_id] ?? 0}
                    onChange={(e) => setAvct({ ...avct, [p.poste_id]: e.target.value })}
                    className="w-20 border border-[#E0E0DB] px-2 py-1 text-right" data-testid={`avct-${p.poste_id}`} />
                  <span>%</span>
                </label>
              </li>
            ))}
          </ul>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-xs text-[#6B6B6B]">Mois de la situation
              <input type="month" value={mois} onChange={(e) => setMois(e.target.value)} disabled={!lot.peut_deposer}
                className="mt-1 w-full border border-[#E0E0DB] px-2 py-1 text-sm text-[#2C2C2C]" />
            </label>
            <label className="text-xs text-[#6B6B6B]">Commentaire pour l'architecte (facultatif)
              <input type="text" value={commentaire} onChange={(e) => setCommentaire(e.target.value)} maxLength={500}
                disabled={!lot.peut_deposer} className="mt-1 w-full border border-[#E0E0DB] px-2 py-1 text-sm text-[#2C2C2C]" />
            </label>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <p className="text-sm text-[#2C2C2C]">
              Travaux du mois : <strong>{fmtEur(montantMois)} HT</strong>
              <span className="block text-xs text-[#6B6B6B]">
                Montant indicatif avant retenues. L'architecte vérifie et certifie sous {delai} jours.
              </span>
            </p>
            <Button className="rounded-none bg-[#C4773B] hover:bg-[#A85F25] text-white gap-2" disabled={envoi || !lot.peut_deposer}
              onClick={deposer} data-testid={`deposer-${lot.lot_code}`}>
              <PaperPlaneTilt size={16} /> {ouverte?.statut === 'deposee' || ouverte?.statut === 'rejetee' ? 'Déposer à nouveau' : 'Déposer ma situation du mois'}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

export default function EntrepriseSituations({ chantierId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(() => {
    axios.get(`${API_URL}/api/chantiers/${chantierId}/mes-situations`, { withCredentials: true })
      .then(({ data: d }) => { setData(d); setErreur(null); })
      .catch((err) => setErreur(err.response?.data?.detail || "Impossible d'afficher vos situations pour le moment."))
      .finally(() => setLoading(false));
  }, [chantierId]);

  useEffect(() => { setLoading(true); charger(); }, [charger]);

  if (loading) return <div className="space-y-3">{[...Array(2)].map((_, i) => <Skeleton key={i} className="h-32" />)}</div>;
  if (erreur) return <p className="text-sm text-[#6B6B6B] border border-[#E0E0DB] bg-white p-4">{erreur}</p>;
  if (!data) return null;

  return (
    <div className="space-y-5 max-w-4xl" data-testid="entreprise-situations">
      <div>
        <h2 className="text-lg text-[#2C2C2C] flex items-center gap-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
          <CurrencyEur size={20} className="text-[#C75B39]" /> Mes situations
        </h2>
        <p className="text-sm text-[#6B6B6B]">
          Chaque mois, déposez votre situation à partir de votre marché : indiquez l'avancement de chaque poste.
          L'architecte la vérifie et la certifie sous {data.delai_verification_jours} jours ; le paiement est attendu
          {' '}{data.delai_paiement_jours} jours après la certification.
        </p>
      </div>

      {data.lots.length === 0 ? (
        <p className="text-sm text-[#6B6B6B] border border-[#E0E0DB] bg-white p-4">
          Aucun lot ne vous est attribué sur ce chantier pour l'instant.
        </p>
      ) : data.lots.map((lot) => (
        <Depot key={`${lot.marche_id}-${lot.situation_ouverte?.id || 'n'}-${lot.situation_ouverte?.statut || ''}`}
          chantierId={chantierId} lot={lot} delai={data.delai_verification_jours} onDone={charger} />
      ))}

      <section className="border border-[#E0E0DB] bg-white" data-testid="entreprise-situations-historique">
        <h3 className="px-4 py-3 border-b border-[#E0E0DB] text-sm font-bold uppercase tracking-wider text-[#6B6B6B]">Historique</h3>
        {data.situations.length === 0 ? (
          <p className="text-sm text-[#9A9A9A] p-4">Aucune situation déposée pour l'instant.</p>
        ) : (
          <ul className="divide-y divide-[#E0E0DB]">
            {data.situations.map((s) => (
              <li key={s.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#2C2C2C]">Situation n° {s.numero} · {s.mois_libelle || moisFr(s.mois)}</p>
                  <p className="text-xs text-[#6B6B6B]">
                    Lot {s.lot_code}{s.deposee_le ? ` · déposée le ${fmtDate(s.deposee_le)}` : ''}
                    {s.certifiee ? ` · Certifiée le ${fmtDate(s.certifiee_le)}` : ''}
                  </p>
                  {s.motif_rejet && <p className="text-xs text-[#8B3A3A]">Motif : {s.motif_rejet}</p>}
                </div>
                <div className="sm:text-right">
                  <p className="text-sm font-bold text-[#2C2C2C]">{fmtEur(s.montant_net_ttc)} TTC</p>
                  <p className="text-xs flex sm:justify-end items-center gap-1">
                    {s.payee ? <CheckCircle size={12} className="text-[#2D6A4F]" /> : <Clock size={12} className="text-[#6B6B6B]" />}
                    <span className={s.payee ? 'text-[#2D6A4F]' : 'text-[#6B6B6B]'}>
                      {s.statut_libelle}
                      {s.certifiee && !s.payee && s.echeance_paiement ? ` · paiement attendu vers le ${fmtDate(s.echeance_paiement)}` : ''}
                    </span>
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
